import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Grid, H1, Paragraph, InputText } from '@increasecard/typed-components';
import { Shop, LoadableStatus, Loadable, idle } from '@card/types';
import { useShallowEqualSelector } from '../../../utils/reduxUtils';
import styled from 'styled-components';
import Shops from '../../../actions/shopsActions';
import { CreateShopModal } from './CreateShopModal';
import { UsersActions } from '../../../actions/userActions';
import { ShopsEditModal } from './ShopsEditModal';
import { BusinessNameForm } from './BusinessNameForm';
import { SmallLink, Services, useModal } from '@card/commonlib';

type ModifyShopNamePayloadType = {
  id: string;
  name: string;
};

type DestroyShopPayloadType = {
  id: string;
  position: number;
};

const SettingsContainer = styled(Grid)`
  margin-top: 1rem;
  align-items: end;

  & label {
    white-space: unset;
  }
`;

export function BusinessSettings() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const shopsEditModal = useModal();
  const createShopModal = useModal();
  const [createShopState, setCreateShopState] = useState<Loadable<undefined>>(idle());

  const shops = useShallowEqualSelector((state) => state.shops.shops);
  const credRows = useShallowEqualSelector((state) => state.credentials.credRows);

  useEffect(() => {
    dispatch(Shops.fetch());
    dispatch(UsersActions.fetch());
  }, [dispatch]);

  const shopsSaved = (shops: Shop[]) => dispatch(Shops.saveShopsMethods(shops));
  const changeShopName = (params: ModifyShopNamePayloadType) =>
    dispatch(Shops.modifyShopName(params));
  const destroyShop = (params: DestroyShopPayloadType) => dispatch(Shops.destroyShop(params));

  const createShop = (name: string) => {
    setCreateShopState({
      status: LoadableStatus.Fetching
    });
    Services.createShop({ name })
      .then(() => {
        setCreateShopState({
          status: LoadableStatus.Fetched,
          data: undefined
        });
        dispatch(Shops.fetch());
        createShopModal.close();
      })
      .catch((response) => {
        setCreateShopState({
          status: LoadableStatus.Error,
          error: new Error(response.data?.message || 'Ha ocurrido un error al crear la sucursal')
        });
      });
  };

  return (
    <>
      <H1 style={{ marginBottom: '8px' }}>{t(`abm:Shops:title`)}</H1>
      <Paragraph style={{ marginBottom: '16px' }}>{t(`abm:Shops:description`)}</Paragraph>
      <SettingsContainer rowGap={3}>
        <div className="grid-span-desktop-6">
          <BusinessNameForm />
        </div>
        <div className="grid-span-desktop-2">
          <InputText
            htmlSize="4"
            id="ShopsInput"
            inline
            label={t(`abm:Shops:shopsCount`)}
            readOnly
            value={shops.data?.length || 0}
          />
        </div>
        <div
          className="grid-span-desktop-2 grid-start-desktop-11"
          style={{ height: '100%', display: 'flex', alignItems: 'flex-end' }}
        >
          <SmallLink
            as="button"
            disabled={shops.status !== LoadableStatus.Fetched}
            onClick={() => shopsEditModal.open()}
            style={{ marginBottom: '8px' }}
          >
            {t(`abm:Shops:shopsLink`)}
          </SmallLink>
        </div>
      </SettingsContainer>
      {shops.data && (
        <ShopsEditModal
          credentialsList={credRows}
          onChangeName={changeShopName}
          onClose={() => shopsEditModal.close()}
          onCreateShop={() => {
            shopsEditModal.close();
            createShopModal.open();
          }}
          onDestroyShop={destroyShop}
          onSave={(shops: Shop[]) => {
            shopsSaved(shops);
            shopsEditModal.close();
          }}
          shops={shops.data}
          visible={shopsEditModal.isOpen}
        />
      )}
      <CreateShopModal
        error={
          createShopState.status === LoadableStatus.Error ? createShopState.error.message : null
        }
        loading={createShopState.status === LoadableStatus.Fetching}
        onCancel={() => createShopModal.close()}
        onConfirm={createShop}
        visible={createShopModal.isOpen}
      />
    </>
  );
}
